import { useEffect, useState } from 'react';
import { t } from 'i18next';
import { useForm, UseFormReturnType } from '@mantine/form';
import { useSetupStore } from '../../store';
import { useLoadState, useNotify } from '@/hooks';
import { useStore } from '@/store';
import { stringifyState } from '@morph-mapper/utils';
import {
  useCategoriesWithFilter,
  useCreateTemplate as useCreateTemplateQuery,
  useSchemasQuery,
} from '@/api';
import { FormValues, TransformFormValues, TransformValues } from './types';
import { SchemaSource } from '@morph-mapper/types';
import {
  EdiFormValues,
  SourceFormValues,
  useCategoriesOptions,
  useEdiOptions,
  useSourceOptions,
  useTemplateBuilder,
} from '../../hooks';
import { resolveSchemaId } from './schema-selection';

export const useCreateTemplate = () => {
  /* Global State */
  const [setId] = useStore(({ config: c }) => [c.setId]);
  /* Context State */
  const [
    operatorId,
    domain,
    isOpen,
    step,
    setOpen,
    setStep,
    setSelectedTemplateId,
  ] = useSetupStore((s) => [
    s.getOperatorId(),
    s.getDomain(),
    s.isCreateModalOpen,
    s.currentStep,
    s.setIsCreateModalOpen,
    s.setCurrentStep,
    s.setSelectedTemplateId,
  ]);
  /* Local State */
  const [template, setTemplate] = useState<any>(undefined);
  /* Hooks */
  const { error } = useNotify();
  const { load } = useLoadState();
  const { buildTemplate } = useTemplateBuilder(operatorId);
  const form = useForm<FormValues, TransformValues>({
    validate: {
      name: (value) =>
        value.trim() === '' ? t('message.error.required') : null,
      categoryId: (value) =>
        value === undefined ? t('message.error.required') : null,
      type: (value) =>
        value === undefined ? t('message.error.required') : null,
      variant: (value) =>
        value === undefined ? t('message.error.required') : null,
      schemaId: (value) =>
        value === undefined ? t('message.error.required') : null,
      ediVariables: {
        user: (value) => (value === '' ? t('message.error.required') : null),
        password: (value) =>
          value === '' ? t('message.error.required') : null,
        endpoint: (value) =>
          value === '' ? t('message.error.required') : null,
      },
    },
    transformValues: (values) => {
      const { ediType, ediVariables } = values;

      return {
        ...values,
        ediVariables: transformEdiVariables(ediType, ediVariables),
      };
    },
  });
  const { getCategoryOptions } = useCategoriesOptions();
  // Due to typing limitations we cast the form to a subset of the form values.
  const { getSourceOptions, handleSourceChange } = useSourceOptions(
    form as unknown as UseFormReturnType<SourceFormValues & EdiFormValues>,
  );
  const {
    getEdiTypes,
    getEdiInputs,
    handleEdiOptionChange,
    transformEdiVariables,
  } = useEdiOptions(form as unknown as UseFormReturnType<EdiFormValues>);
  /* Queries */
  const { data: schemas = [], isFetched: schemasFetched } = useSchemasQuery({
    domain,
    status: 'active',
  });
  const { data: categories, isFetched: categoriesFetched } =
    useCategoriesWithFilter({ operatorId, domain });
  const {
    mutate: createTemplate,
    isSuccess: createSuccess,
    data: templateId,
  } = useCreateTemplateQuery();

  /**
   * Handlers
   */
  const loadInitialValues = (categoryId: string) => {
    form.setValues({
      name: '',
      description: '',
      priority: 0,
      categoryId,
      type: undefined,
      variant: undefined,
      schemaId: undefined,
      inputsource: SchemaSource.File,
      ediType: undefined,
      ediVariables: {},
    });
  };

  const formatSchemaLabel = (value: string) => {
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  const getSchemaTypeOptions = () => {
    return [...new Set(schemas.map((schema) => schema.type))].map((type) => ({
      key: type,
      value: type,
      label: formatSchemaLabel(type),
    }));
  };

  const getSchemaVariantOptions = () => {
    return [
      ...new Set(
        schemas
          .filter((schema) =>
            form.values.type ? schema.type === form.values.type : true,
          )
          .map((schema) => schema.variant),
      ),
    ].map((variant) => ({
      key: variant,
      value: variant,
      label: formatSchemaLabel(variant),
    }));
  };

  const getSchemaOptions = () => {
    if (!form.values.type || !form.values.variant) return [];

    return schemas
      .filter(
        (schema) =>
          schema.type === form.values.type &&
          schema.variant === form.values.variant,
      )
      .map((schema) => ({
        value: schema.id,
        label: schema.name,
      }));
  };

  const submit = (values: TransformFormValues) => {
    const selectedSchema = schemas.find(
      (schema) => schema.id === values.schemaId,
    );

    if (!selectedSchema) {
      error(t('message.error.schemaNotFound'));
      return;
    }

    const save = buildTemplate(values.name, selectedSchema);

    if (save === undefined) {
      error(t('message.error.templateCreationFailed'));
      return;
    }

    createTemplate({
      ...values,
      operatorId,
      deployPath: null,
      templatetype: 'MorphMapper',
      domain,
      enabled: false,
      isStatic: false,
      save: stringifyState(save),
    });

    setTemplate(save);
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  /**
   * Effects
   */
  // Wait for the categories to be loaded before setting the initial values
  useEffect(() => {
    if (schemasFetched === false) return;
    if (categoriesFetched === false) return;

    const defaultCategory = categories.find(
      (category) => category.name === 'default',
    );
    // This should never happen, the default category is managed by the backend
    if (defaultCategory === undefined) {
      error(t('message.error.defaultCategoryMissing'));
      setOpen(false);
      return;
    }

    loadInitialValues(defaultCategory.id);
  }, [categoriesFetched, categories, schemasFetched]);

  useEffect(() => {
    const typeOptions = getSchemaTypeOptions().map((option) => option.value);

    if (
      form.values.type !== undefined &&
      !typeOptions.includes(form.values.type)
    ) {
      form.setFieldValue('type', undefined);
    }
  }, [form.values.type, schemas]);

  useEffect(() => {
    const variantOptions = getSchemaVariantOptions().map(
      (option) => option.value,
    );
    if (
      form.values.variant !== undefined &&
      !variantOptions.includes(form.values.variant)
    ) {
      form.setFieldValue('variant', undefined);
    }
  }, [form.values.type, form.values.variant, schemas]);

  useEffect(() => {
    const schemaOptions = getSchemaOptions();
    const nextSchemaId = resolveSchemaId(form.values.schemaId, schemaOptions);

    // Mantine emits a form update even when assigning undefined to an already
    // empty field. Repeating that assignment can trigger React's depth guard.
    if (nextSchemaId !== form.values.schemaId) {
      form.setFieldValue('schemaId', nextSchemaId);
    }
  }, [form.values.schemaId, form.values.type, form.values.variant, schemas]);

  // On successful template creation we redirect the user to the newly created template
  useEffect(() => {
    if (createSuccess === false) return;

    setSelectedTemplateId(templateId);
    setId(templateId);
    load(template);
    setStep(step + 1);
    setOpen(false);
  }, [createSuccess]);

  return {
    form,
    getSchemaTypeOptions,
    getSchemaVariantOptions,
    getSchemaOptions,
    getSourceOptions,
    getCategoryOptions,
    getEdiTypes,
    getEdiInputs,
    handleEdiOptionChange,
    handleSourceChange,
    isOpen,
    handleCloseModal,
    submit,
  };
};
